import React, { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router-dom";

import Preloader from "~/components/Preloader";
import Button from "~/shared/ui/Fields/Button";
import MultiInput from "~/shared/ui/Fields/MultiSelect";
import { NOTIFICATIONS } from "~/constans/notifications";
import { useGetLocationData } from "~/hooks/locationData";
import {
  DataTrainingProgram,
  SelectOptions,
  TLevel,
  TrainingProgram,
} from "~/pages/Editor/EditorCatalogEducationalPrograms/types";
import { useGetAllLevels, useGetTrainingProgramById } from "~/pages/Editor/EditorCatalogEducationalPrograms/utils";
import { OpenModal } from "~/pages/Editor/type";
import {
  useAddNotificationMutation,
  useCreateCurriculumPositionStudyProgramsMutation,
  useEditCurriculumPositionStudyProgramsMutation,
} from "~/schemas/generated/graphql";
import { FormStatus } from "~/shared/lib/statuses";

import HistoricalData from "./HistoricalData";
import useGetAllFilteredPositionsForSelect from "./hooks/useGetAllFilteredPositionsForSelect/useGetAllFilteredPositionsForSelect";
import TableControl from "./TableControl";
import { useAddCourse, useDeleteCourse, useUpdateCourse } from "./hooks/index";
import { MemoizedLevel } from "./Level/index";
import {
  ChangedCourseDetails,
  DeletingCourse,
  IGraphQLLevel,
  LevelCourse,
  PositionProgramsActionEnum,
} from "./types";

import s from "./ProgramsManagement.less";

type Params = {
  id: string;
};

type dataSubmit = {
  PositionsIds: SelectOptions[];
};

const ProgramsManagement = () => {
  const params = useParams<Params>();
  const { id } = params;
  const { t } = useTranslation();
  const { openType } = useGetLocationData();
  const errorRef = useRef(null);
  const history = useHistory();
  const [addNotificationMutation] = useAddNotificationMutation({});

  const isViewing = openType === OpenModal.View;

  const {
    positions,
    intersectionObserverPositions,
    searchTextPositions,
  }: {
    positions: SelectOptions[];
    intersectionObserverPositions: any;
    searchTextPositions: any;
  } = useGetAllFilteredPositionsForSelect();

  const [usedCoursesId, setUsedCoursesId] = useState<string[]>([]);

  const dataTrainingPositionById = useGetTrainingProgramById(id);

  const queryLevels: IGraphQLLevel[] = useGetAllLevels().levels;

  const [collectedData, setCollectedData] = useState<TrainingProgram>({
    PositionGroupId: null,
    PositionsIds: [],
    levels: [],
  });

  const [editingTrainingProgram, setEditingTrainingProgram] =
    useState<DataTrainingProgram>();
  const [isLoadingTrainingPosition, setIsLoadingTrainingPosition] =
    useState<boolean>();
  const [errorPositionsIds, setErrorPositionsIds] = useState(null);

  const [createProgram] = useCreateCurriculumPositionStudyProgramsMutation({
    onCompleted() {
      addNotificationMutation({
        variables: {
          type: NOTIFICATIONS.success,
          content: t("common.сhange_was_successful"),
        },
      });
    },
    onError(vals) {
      addNotificationMutation({
        variables: {
          type: NOTIFICATIONS.error,
          content: vals["message"],
        },
      });
    },
  });

  const [editProgram] = useEditCurriculumPositionStudyProgramsMutation({
    onCompleted() {
      addNotificationMutation({
        variables: {
          type: NOTIFICATIONS.success,
          content: t("common.сhange_was_successful"),
        },
      });
      dataTrainingPositionById.refetch({ PositionGroupId: id });
    },
    onError(vals) {
      addNotificationMutation({
        variables: {
          type: NOTIFICATIONS.error,
          content: vals["message"],
        },
      });
    },
  });

  const { handleSubmit, control, setValue, watch } = useForm({
    defaultValues: {
      actualOn: "",
      PositionsIds: [],
      PositionGroupId: null,
      levels: queryLevels.map((level) => ({ id: level.id, courses: [] })),
    },
  });

  const addCourseToCollection = (
    levelId: string,
    addingCourse: LevelCourse
  ) => {
    setErrorPositionsIds(null);
    setUsedCoursesId((usedCoursesId) => [
      ...usedCoursesId,
      addingCourse.course.value,
    ]);
    useAddCourse({ setCollectedData, addingCourse, levelId });
  };

  const deleteCourseFromCollection = (
    levelId: string,
    deletedCourse: DeletingCourse
  ) => {
    const { id } = deletedCourse;
    const updatedUsedCoursesId = usedCoursesId.filter(
      (course) => course !== id
    );
    setUsedCoursesId(updatedUsedCoursesId);
    useDeleteCourse(setCollectedData, deletedCourse, levelId);
  };

  const updateCourseInCollection = (
    levelId,
    updatedCourse: ChangedCourseDetails,
    oldCourseId: string
  ) => {
    if (updatedCourse && updatedCourse.course && updatedCourse.status) {
      setUsedCoursesId((usedCoursesId) => {
        const filteredCoursesId = usedCoursesId.filter(
          (courseId) => courseId !== oldCourseId
        );
        return [...filteredCoursesId, updatedCourse.course.value];
      });

      useUpdateCourse({
        setCollectedData,
        updatedCourse,
        levelId,
        oldCourseId,
      });
    }
  };

  const onSubmit = (data: dataSubmit) => {
    if (data.PositionsIds.length < 1) {
      errorRef.current.scrollIntoView({ behavior: "smooth" });
      setErrorPositionsIds("Ошибка: - Выберите должность!");
    } else {
      const dataPositionsIds = data.PositionsIds.map((el) => el.value);

      const filteredLevels = collectedData.levels
        .filter((level) => level.status !== FormStatus.inactive)
        .map((level) =>
          level.courses.length <= 0
            ? { ...level, action: PositionProgramsActionEnum.delete }
            : {
                ...level,
                action: id
                  ? PositionProgramsActionEnum.edit
                  : PositionProgramsActionEnum.create,
              }
        );

      const levelsRequest = filteredLevels.map((level) => {
        const updatedCourses = level.courses.map((course) => {
          const {
            name,
            __typename,
            isDisabled,
            certificateValidityPeriod,
            value,
            label,
            ...rest
          } = course;
          return rest;
        });
        return { id: level.id, action: level.action, courses: updatedCourses };
      });

      if (id) {
        editProgram({
          variables: {
            PositionGroupId: id,
            PositionIds: dataPositionsIds,
            levels: levelsRequest as any[],
          },
        });
      } else {
        const isEmptyLevels = levelsRequest.every(
          (level) => level.action === PositionProgramsActionEnum.delete
        );
        if (isEmptyLevels) {
          errorRef.current.scrollIntoView({ behavior: "smooth" });
          setErrorPositionsIds("Нельзя создать пустую программу");
          return;
        }
        createProgram({
          variables: {
            PositionIds: dataPositionsIds,
            levels: levelsRequest as any[],
          },
        }).then(() => history.replace("/editor/cep/ptp/"));
      }
    }
  };

  useEffect(() => {
    setErrorPositionsIds(null);
  }, [collectedData.PositionsIds]);

  useEffect(() => {
    if (id && editingTrainingProgram && queryLevels) {
      const currentLevels = editingTrainingProgram.levels;
      setCollectedData({
        levels: queryLevels
          .map((level) => {
            const matchingLevel = currentLevels.find(
              (currentLevel) => currentLevel.id === level.id
            );

            if (matchingLevel) {
              return {
                id: level.id,
                name: level.name,
                status: level.status,
                courses: matchingLevel.courses,
                sequenceNumber: level.sequenceNumber,
                isCertification: level.isCertification,
              };
            } else {
              return {
                id: level.id,
                name: level.name,
                status: level.status,
                sequenceNumber: level.sequenceNumber,
                isCertification: level.isCertification,
                courses: [],
              };
            }
          })
          .sort((a, b) => a.sequenceNumber - b.sequenceNumber) as TLevel[],
      });
    } else {
      setCollectedData({
        ...collectedData,
        levels: queryLevels
          .map((level) => ({
            id: level.id,
            name: level.name,
            status: level.status,
            courses: [],
            sequenceNumber: level.sequenceNumber,
          }))
          .sort((a, b) => a.sequenceNumber - b.sequenceNumber) as TLevel[],
      });
    }
  }, [queryLevels, editingTrainingProgram]);

  useEffect(() => {
    if (id) {
      const isLoading = dataTrainingPositionById.loading;
      const dataCurrentTrainingProgram =
        dataTrainingPositionById.trainingProgram;
      setIsLoadingTrainingPosition(isLoading);
      setEditingTrainingProgram(dataCurrentTrainingProgram);
    }
  }, [id, dataTrainingPositionById]);

  useEffect(() => {
    if (editingTrainingProgram) {
      const programLevels = editingTrainingProgram.levels;

      const selectorOptions = editingTrainingProgram.Positions.map(
        (position) => {
          return {
            label: position.name,
            value: position.id,
          };
        }
      );

      const updatedUsedCoursesId = programLevels.reduce(
        (accumulator, level) => {
          const levelCoursesIds = level.courses.map((course) => course.id);
          return accumulator.concat(levelCoursesIds);
        },
        []
      );

      setUsedCoursesId(updatedUsedCoursesId);

      setValue("PositionsIds", selectorOptions);
      setErrorPositionsIds(null);
    }
  }, [editingTrainingProgram]);

  return (
    <div className={s.wrapper_edit}>
      <div className={s.header_edit}>
        <div>
          <span ref={errorRef} className={s.title_edit}>
            {!id
              ? t("patp.editor")
              : !isViewing
              ? t("patp.edit_training_program")
              : t("patp.viewing_training_program")}
          </span>
        </div>
        <TableControl />
      </div>
      {isViewing && (
        <HistoricalData
          dealerId={id}
          control={control}
          refetch={dataTrainingPositionById.refetch}
          watch={watch}
        />
      )}
      {!isLoadingTrainingPosition ? (
        <form onSubmit={handleSubmit(onSubmit)} className={s.form}>
          <div className={s.item}>
            <span>{t("patp.position")}</span>
            <MultiInput
              name={"PositionsIds"}
              label={t("patp.position")}
              control={control}
              isDisabled={isViewing}
              options={positions}
              onScrollBottom={intersectionObserverPositions}
              getSearchText={searchTextPositions}
              customInput={{ height: "50px" }}
              customLabelClass={s.multi_input_label}
              menuZIndex={5}
            />
            {errorPositionsIds && (
              <span className={s.error_message}>{errorPositionsIds}</span>
            )}
          </div>
          {collectedData.levels.map((level) => (
            <MemoizedLevel
              control={control}
              level={level}
              courses={level.courses}
              key={level.id}
              addCourseToCollection={addCourseToCollection}
              deleteCourseFromCollection={deleteCourseFromCollection}
              updateCourseInCollection={updateCourseInCollection}
              isViewing={isViewing}
              usedCoursesId={usedCoursesId}
              watch={watch}
              refetch={dataTrainingPositionById.refetch}
            />
          ))}
          {!isViewing && <Button isForm={true} txtBtn={t("ui.save")} />}
        </form>
      ) : (
        <Preloader />
      )}
    </div>
  );
};

export default ProgramsManagement;
